import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography,
} from '@mui/material';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import {
  deleteFloorPlan, getFloorPlanDeletionImpact, type FloorPlanDeletionImpact,
} from '../services/floorplan-editor.service';

interface Props {
  readonly open: boolean;
  readonly floorPlanId: string | undefined;
  readonly onRemoved: (storey: number) => void;
  readonly onCancel: () => void;
}

/**
 * Removes a floorplan so the floor can be started again — from GeoJSON, the sample floor, or an
 * image — after saying what it costs.
 *
 * A plan with nothing bookable on it, like a sample floor nobody has linked to rooms yet, is
 * removed with one confirmation. One with bookable desks lists what changes — the capacity each
 * room loses, the bookings left on desks no longer drawn anywhere — and has to be acknowledged,
 * which the server insists on as well.
 */
const RemoveFloorPlanDialog = ({
  open, floorPlanId, onRemoved, onCancel,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [impact, setImpact] = useState<FloorPlanDeletionImpact | undefined>();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    let current = true;

    if (open && floorPlanId) {
      setLoading(true);
      setFailed(false);
      setAcknowledged(false);
      setImpact(undefined);

      getFloorPlanDeletionImpact(floorPlanId, keycloak)
        .then((result) => {
          if (!current) return;
          setImpact(result);
          setFailed(result === undefined);
        })
        .catch(() => {
          if (current) setFailed(true);
        })
        .finally(() => {
          if (current) setLoading(false);
        });
    }

    return (): void => {
      current = false;
    };
  }, [open, floorPlanId, keycloak]);

  const confirmable = !!impact && !loading && !removing && (!impact.hasImpact || acknowledged);

  const remove = async (): Promise<void> => {
    if (!floorPlanId || !impact) return;

    setRemoving(true);

    try {
      const result = await deleteFloorPlan(floorPlanId, impact.hasImpact && acknowledged, keycloak);

      if (result === 'removed') {
        onRemoved(impact.storey);
      } else {
        setFailed(true);
      }
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t('floorplan-editor-remove-title', { storey: impact?.storey ?? '' })}</DialogTitle>
      <DialogContent>
        {loading && <CircularProgress size={20} />}
        {failed && <Alert severity="error">{t('floorplan-editor-remove-failed')}</Alert>}

        {impact && (
          <Stack spacing={1.5}>
            <Typography variant="body2">{t('floorplan-editor-remove-explanation')}</Typography>

            {impact.hasImpact ? (
              <>
                <Alert severity="warning">
                  <Stack spacing={0.5}>
                    <span>{t('floorplan-editor-remove-bookable', { desks: impact.bookableDesks })}</span>
                    {impact.rooms.map((room) => (
                      <span key={room.roomId}>
                        {t('floorplan-editor-remove-room', {
                          room: room.roomName, desks: room.desks, before: room.capacityBefore, after: room.capacityAfter,
                        })}
                      </span>
                    ))}
                    {impact.futureBookings > 0 && (
                      <span>{t('floorplan-editor-remove-bookings', { bookings: impact.futureBookings, people: impact.people })}</span>
                    )}
                  </Stack>
                </Alert>
                <FormControlLabel
                  control={<Checkbox checked={acknowledged} onChange={(event): void => setAcknowledged(event.target.checked)} />}
                  label={String(t('floorplan-editor-remove-acknowledge'))}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('floorplan-editor-remove-nothing-bookable', { desks: impact.desks })}
              </Typography>
            )}

            {impact.hasDraft && <Typography variant="body2">{t('floorplan-editor-remove-draft')}</Typography>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!confirmable}
          onClick={(): void => {
            remove().catch(() => setFailed(true));
          }}
        >
          {t('floorplan-editor-remove-confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveFloorPlanDialog;
